
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Client } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ClientAvatar from "./table/ClientAvatar";
import ClientContacts from "./table/ClientContacts";
import ClientTableActions from "./table/ClientTableActions";
import ClientPackageHoursBadge from "./ClientPackageHoursBadge";
import ClientCoursesBadge from "./ClientCoursesBadge";

interface ClientsTableProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
}

const ClientsTable = ({ clients, onEdit, onDelete }: ClientsTableProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [creatingAccount, setCreatingAccount] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const checkIsAdmin = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        if (!error && data) {
          setIsAdmin(data.role === 'admin');
        }
      } catch (error) {
        console.error("Errore nel controllo del ruolo admin:", error);
      }
    };
    checkIsAdmin();
  }, [user]);

  const handleViewClient = (clientId: string) => {
    navigate(`/admin/clients/${clientId}`);
  };

  const createClientAccount = async (client: Client) => {
    if (!client.email) {
      toast({
        title: "Errore",
        description: "Il cliente deve avere un'email per creare un account",
        variant: "destructive"
      });
      return;
    }

    try {
      setCreatingAccount(client.id);

      const { data: usersList, error: checkError } = await supabase.auth.admin.listUsers();

      const existingUser = usersList?.users?.find(u => u.email === client.email);
      if (existingUser) {
        const { error: updateError } = await supabase
          .from('clients')
          .update({ auth_user_id: existingUser.id })
          .eq('id', client.id);
        
        if (updateError) throw updateError;

        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          client.email, 
          { redirectTo: window.location.origin + '/reset-password' }
        );
        
        if (resetError) throw resetError;
        
        toast({
          title: "Account collegato",
          description: `L'account esistente per ${client.email} è stato collegato al cliente ${client.first_name} ${client.last_name}. Un'email di reset password è stata inviata.`
        });
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email: client.email,
        password: Math.random().toString(36).slice(-12),
        options: {
          data: {
            client_registration: "true",
            first_name: client.first_name,
            last_name: client.last_name,
            existing_client_id: client.id,
            admin_created: "true"
          }
        }
      });
      
      if (error) throw error;
      
      const { error: updateError } = await supabase
        .from('clients')
        .update({ auth_user_id: data.user?.id })
        .eq('id', client.id);
      
      if (updateError) throw updateError;
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        client.email,
        { redirectTo: window.location.origin + '/reset-password' }
      );
      
      if (resetError) throw resetError;
      
      toast({
        title: "Account creato",
        description: `Account creato per ${client.first_name} ${client.last_name}. Un'email è stata inviata con le istruzioni per impostare la password.`
      });
    } catch (error: any) {
      console.error("Errore durante la creazione dell'account:", error);
      toast({
        title: "Errore",
        description: error.message || "Errore durante la creazione dell'account",
        variant: "destructive"
      });
    } finally {
      setCreatingAccount(null);
    }
  };

  // Risolviamo il problema di tipizzazione con il filtro dei clienti
  const filteredClients: Client[] = isAdmin 
    ? (clients || []) 
    : (clients || []).filter(client => {
        // Verifichiamo che user esista prima di accedere al suo id
        return client.auth_user_id === (user?.id || null);
      });

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">Avatar</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead className="w-[150px]">Telefono</TableHead>
            <TableHead>Ore Residue</TableHead>
            <TableHead>Corsi</TableHead>
            {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredClients.map(client => (
            <TableRow key={client.id} className="border-b hover:bg-muted/50 transition-colors">
              <TableCell>
                <ClientAvatar
                  firstName={client.first_name || ''}
                  lastName={client.last_name || ''}
                  avatarUrl={client.avatar_url}
                  colorCode={client.color_code}
                  onClick={() => handleViewClient(client.id)}
                />
              </TableCell>
              <TableCell className="font-medium">
                <div 
                  onClick={() => handleViewClient(client.id)} 
                  className="cursor-pointer hover:text-primary transition-colors underline"
                >
                  {client.first_name} {client.last_name}
                </div>
                <div className="text-xs text-muted-foreground">{client.email || ""}</div>
                {client.auth_user_id && (
                  <div className="text-xs text-green-600 font-medium mt-1">
                    Account attivo
                  </div>
                )}
              </TableCell>
              <TableCell>
                <ClientContacts telefono={client.telefono} />
              </TableCell>
              <TableCell>
                <ClientPackageHoursBadge clientId={client.id} />
              </TableCell>
              <TableCell>
                <ClientCoursesBadge clientId={client.id} />
              </TableCell>
              {isAdmin && (
                <TableCell className="text-right">
                  <ClientTableActions
                    client={client}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onCreateAccount={createClientAccount}
                    isCreatingAccount={creatingAccount}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ClientsTable;
